/* eslint zillow/react/forbid-prop-types: 0 */

import PropTypes from 'prop-types';
import { T_SHIRT_SIZES } from '../constants';
var tShirtSize = PropTypes.oneOf(Object.values(T_SHIRT_SIZES));
var inlineMediaQuery = function inlineMediaQuery(propType) {
  return PropTypes.oneOfType([propType, PropTypes.shape({
    default: propType,
    xs: propType,
    xs_lte: propType,
    xs_eq: propType,
    xs_gte: propType,
    sm: propType,
    sm_lte: propType,
    sm_eq: propType,
    sm_gte: propType,
    md: propType,
    md_lte: propType,
    md_eq: propType,
    md_gte: propType,
    lg: propType,
    lg_lte: propType,
    lg_eq: propType,
    lg_gte: propType,
    xl: propType,
    xl_lte: propType,
    xl_eq: propType,
    xl_gte: propType,
    xxl: propType,
    xxl_lte: propType,
    xxl_eq: propType,
    xxl_gte: propType
  })]);
};

/**
 * Parses classes and instances for useful information.
 */
export function getComponentStats(component) {
  var isInstance = !!component.type;
  var type = isInstance ? component.type : component;
  var styledComponentId = type.styledComponentId;
  var isStyledComponent = !!styledComponentId;
  var foldedComponentIds = type.foldedComponentIds;
  var identifier;
  if (typeof type === 'string') {
    // HTML element
    identifier = type;
  } else {
    // React Component || Styled Component
    identifier = type.name || type.displayName;
  }
  return {
    isInstance: isInstance,
    isStyledComponent: isStyledComponent,
    identifier: identifier,
    styledComponentId: styledComponentId,
    foldedComponentIds: foldedComponentIds
  };
}

/**
 * Helper function for comparing component types.
 */
function inTypeHeirarchy(component, type) {
  var _getComponentStats = getComponentStats(component),
    identifier = _getComponentStats.identifier,
    foldedComponentIds = _getComponentStats.foldedComponentIds;
  var expected = getComponentStats(type);
  if (identifier === expected.identifier) {
    return true;
  }
  if (foldedComponentIds && expected.styledComponentId && foldedComponentIds.indexOf(expected.styledComponentId) >= 0) {
    return true;
  }
  return false;
}

/**
 * Tests if the given component class matches, or is an extension of the given
 * component type.
 */
export function doesComponentExtendStyledComponent(component, type) {
  var _getComponentStats2 = getComponentStats(component),
    isInstance = _getComponentStats2.isInstance,
    isStyledComponent = _getComponentStats2.isStyledComponent;
  if (isInstance || !isStyledComponent) {
    return false;
  }
  return inTypeHeirarchy(component, type);
}

/**
 * Tests if the given component is an instance of the given component type,
 * including superclasses when it is a styled-component.
 */
export function isInstanceOfComponent(component, type) {
  var _getComponentStats3 = getComponentStats(component),
    isInstance = _getComponentStats3.isInstance;
  if (!isInstance) {
    return false;
  }
  return inTypeHeirarchy(component, type);
}
export default {
  /**
   * Custom proptype validator that validates the given prop is an instance
   * of the given component.
   *
   * Usage:
   *
   *     label: instanceOfComponent(Label).isRequired,
   *     control: PropTypes.oneOfType([
   *         instanceOfComponent(Checkbox),
   *         instanceOfComponent(Radio),
   *     ]).isRequired,
   */
  instanceOfComponent: function instanceOfComponent(expected) {
    // Using oneOfType allows us to chain this custom prop-type validator
    return PropTypes.oneOfType([function _instanceOfComponent(props, propName, componentName) {
      if (!isInstanceOfComponent(props[propName], expected)) {
        var _getComponentStats4 = getComponentStats(props[propName]),
          identifier = _getComponentStats4.identifier;
        return new Error("Invalid prop `" + propName + "` supplied to `" + componentName + "`. Component type `" + identifier + "` not allowed.");
      }
      return null;
    }]);
  },
  /**
   * Custom proptype validator that validates the given prop is an extension
   * of the given styled-component class.
   */
  extendsStyledComponent: function extendsStyledComponent(expected) {
    // Using oneOfType allows us to chain this custom prop-type validator
    return PropTypes.oneOfType([function _extendsStyledComponent(props, propName, componentName) {
      if (!doesComponentExtendStyledComponent(props[propName], expected)) {
        var _getComponentStats5 = getComponentStats(props[propName]),
          identifier = _getComponentStats5.identifier;
        return new Error("Invalid prop `" + propName + "` supplied to `" + componentName + "`. Component type `" + identifier + "` not allowed.");
      }
      return null;
    }]);
  },
  mediaQuery: PropTypes.shape({
    xs: PropTypes.object,
    xs_lte: PropTypes.object,
    xs_eq: PropTypes.object,
    xs_gte: PropTypes.object,
    sm: PropTypes.object,
    sm_lte: PropTypes.object,
    sm_eq: PropTypes.object,
    sm_gte: PropTypes.object,
    md: PropTypes.object,
    md_lte: PropTypes.object,
    md_eq: PropTypes.object,
    md_gte: PropTypes.object,
    lg: PropTypes.object,
    lg_lte: PropTypes.object,
    lg_eq: PropTypes.object,
    lg_gte: PropTypes.object,
    xl: PropTypes.object,
    xl_lte: PropTypes.object,
    xl_eq: PropTypes.object,
    xl_gte: PropTypes.object,
    xxl: PropTypes.object,
    xxl_lte: PropTypes.object,
    xxl_eq: PropTypes.object,
    xxl_gte: PropTypes.object
  }),
  tShirtSize: tShirtSize,
  spacing: inlineMediaQuery(PropTypes.oneOfType([PropTypes.number, tShirtSize])),
  ref: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  popperPlacement: PropTypes.oneOf(['auto-start', 'auto', 'auto-end', 'top-start', 'top', 'top-end', 'left-start', 'left', 'left-end', 'right-start', 'right', 'right-end', 'bottom-start', 'bottom', 'bottom-end']),
  inlineMediaQuery: inlineMediaQuery
};